import { useQuery } from "@tanstack/react-query";

import { useState } from "react";
import { api } from "shared/api";
import { AvailableBumbalDate } from "shared/api/middleware-api-client";

// shipment (is delivery) | delivery (is assembly)
type OrderType = "shipment" | "placement";
const months: string[] = [
  "januari",
  "februari",
  "maart",
  "april",
  "mei",
  "juni",
  "juli",
  "augustus",
  "september",
  "oktober",
  "november",
  "december",
];

/* Mapping the available dates from the slots array. */
const availableDates = (data: AvailableBumbalDate[] | undefined) => {
  if (data) {
    return data.map((data) => new Date(data.dateFrom.split(" ")[0]));
  }
  return null;
};

export const useAvailableStandardOrderBumbalDates = ({
  type = "shipment",
  delayedDays = 0,
  setDate,
  selectedDateProp,
  order_id,
}: {
  type: OrderType;
  delayedDays?: number;
  setDate: (date: Date | null) => void;
  selectedDateProp: Date | null;
  order_id: string;
}) => {
  const currentDate = new Date();

  // The pageDate controlling the fetchable dates
  const [pageDate, setPageDate] = useState<Date>(currentDate);

  const [isFirstCall, setIsFirstCall] = useState(true);

  const { data, status, error } = useQuery(
    ["gettingAvailableStandardOrderBumbalDates", pageDate],
    async () => {
      // Copy the date
      const requestDate = new Date(pageDate);

      // If it is the first call, add the delayed days to the date
      if (isFirstCall) {
        requestDate.setDate(requestDate.getDate() + delayedDays);
        setIsFirstCall((prev) => false);
      }

      // Fetch the day, month and year from the date
      const fetchDay = requestDate.getDate(); // Returns the day of the month (1-31)
      const fetchMonth = requestDate.getMonth() + 1; // Returns the month (0-11), so we add 1 to make it (1-12)
      const fetchYear = requestDate.getFullYear();
      const lastDay = new Date(
        requestDate.getFullYear(),
        requestDate.getMonth() + 1,
        0,
      ).getDate();

      // Fetch start and end dates
      const [fetchStartDate, fetchEndDate] = [
        `${fetchYear}-${fetchMonth}-${fetchDay}`,
        `${fetchYear}-${fetchMonth}-${lastDay}`,
      ];

      // Query the available dates from the API
      const { data } = await api().gersAvailableStandardOrderBumbalDates(
        order_id,
        type,
        fetchStartDate,
        fetchEndDate,
      );

      if (data.length > 0 && !selectedDateProp) {
        // If there is a valid date found in the query,
        // and the cart delivery date is null ---> update the cart delivery date

        setDate(new Date(data[0].dateFrom.split(" ")[0]));

        // If first month has no available dates, go to the next month
      } else if (data.length === 0 && isFirstCall) {
        const newDate = new Date(requestDate); // Step 1: Create a new Date object from myDate
        newDate.setMonth(newDate.getMonth() + 1); // Step 2: Add 1 month to the new Date object
        newDate.setDate(1); // Step 3: Set the date to the first day of the month

        //Set page date to the new date
        setPageDate((prev: Date) => newDate);
        setIsFirstCall((prev) => false);
      }

      return data;
    },
    {
      enabled: !!delayedDays && delayedDays > 0,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    },
  );

  const getFirstDate = () => {
    if (!availableDatesContainer || availableDatesContainer.length === 0) {
      return null;
    }
    return availableDatesContainer[0];
  };

  // Example date: December 15 2025 -> total months: December, January, February, March, April, May
  const getNextSixMonths = (): string[] => {
    const startMonthIndex = firstMonth().getMonth(); // 0–11

    return Array.from({ length: 5 }, (_, i) => {
      return months[(startMonthIndex + i) % 12];
    });
  };

  // Get the month number for the very first call
  const firstMonth = () => {
    const myFirstMonth = new Date(currentDate);
    myFirstMonth.setDate(myFirstMonth.getDate() + delayedDays);
    return myFirstMonth;
  };

  const availableDatesContainer = availableDates(data);
  const firstAvailableDate = getFirstDate();
  const availableMonths = getNextSixMonths();

  return {
    data,
    availableDatesContainer,
    availableMonths,
    firstAvailableDate,
    status,
    error,
    setPageDate,
    firstMonth,
    pageDate,
  };
};
